import { trackEvent } from './config'

// Devise answers 401 both for a session that expired and for a sign-in it rejected, and only
// the first means the professional has to sign in again.
const SIGN_IN_PATH = '/professionals/sign_in'

let signingInAgain = false
let pendingPath: string | null = null

export function setPendingPath(path: string | null) {
  pendingPath = path
}

// A closed account may read but not write, and a call site without a catch of its own would
// otherwise fail silently, so ToastProvider shows the server's message for every 403.
export const API_FORBIDDEN_EVENT = 'api:forbidden'
const FORBIDDEN_MESSAGE = 'No tienes permiso para realizar esta acción.'

declare global {
  interface WindowEventMap {
    [API_FORBIDDEN_EVENT]: CustomEvent<string>
  }
}

export function getPathWithoutQuery(url: string): string {
  return url.split('?')[0]
}

function goToSignIn() {
  if (signingInAgain) {
    return
  }
  signingInAgain = true
  trackEvent('Session Expired', { status_code: 401 }, { transport: 'sendBeacon' })
  // Reloading the screen the professional was heading to lets Devise store it as the return
  // location, so signing in lands there instead of on the default page. During a navigation the
  // address bar still shows the screen being left, so the router's destination wins.
  const { pathname, search } = window.location
  window.location.assign(pathname === SIGN_IN_PATH ? SIGN_IN_PATH : (pendingPath ?? `${pathname}${search}`))
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly text: string = '',
  ) {
    super(`Request failed with status ${status}`)
  }
}

function getCsrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  return meta?.content ?? ''
}

function setCsrfToken(token: string) {
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.setAttribute('content', token)
}

function getForbiddenMessage(body: unknown): string {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return FORBIDDEN_MESSAGE
  }
  return typeof body.error === 'string' ? body.error : FORBIDDEN_MESSAGE
}

function getFreshCsrfToken(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('csrf_token' in body)) {
    return null
  }
  return typeof body.csrf_token === 'string' ? body.csrf_token : null
}

// Returns null rather than the raw text when the payload is not JSON: callers cast `body` to the
// error shape they expect, so a string there would make `Object.entries(body)` throw inside their
// own catch. The unparsed payload stays available on ApiError#text.
function parseJson(text: string): { ok: true; value: unknown } | { ok: false } {
  if (text.length === 0) {
    return { ok: true, value: null }
  }
  try {
    return { ok: true, value: JSON.parse(text) }
  } catch {
    return { ok: false }
  }
}

async function request<T>(method: string, url: string, body?: unknown, isRetry = false): Promise<T> {
  const isFormData = body instanceof FormData
  const startedAt = performance.now()
  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
      ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  })
  // duration_ms runs from the fetch call to the response headers arriving: the
  // Rails Request duration for the same call plus the network on both sides.
  const duration_ms = Math.round(performance.now() - startedAt)
  trackEvent('API Request', { status_code: response.status, path: getPathWithoutQuery(url), method, duration_ms })
  // Emitted before the body is parsed: an error page comes back as HTML, and
  // JSON.parse throwing on it is the failure this most needs to see. One call
  // site covers every mutation in the app, including the handlers that have no
  // catch of their own and would otherwise fail silently.
  if (!response.ok) {
    trackEvent('API Error', { status_code: response.status, path: getPathWithoutQuery(url), method, duration_ms })
  }
  if (isRetry) {
    trackEvent('Stale CSRF Token Retried', { status_code: response.status, path: getPathWithoutQuery(url), method })
  }
  // Without this every later call answers 401 too, and a screen that reloads on its own — the
  // calendar refetching each range the professional moves to — keeps a dead session going.
  if (response.status === 401 && !url.startsWith(SIGN_IN_PATH)) {
    goToSignIn()
  }
  const text = await response.text()
  const parsed = parseJson(text)
  const json = parsed.ok ? parsed.value : null
  // Rails rejected the token before the action ran — a remember-me sign-in replaced the session
  // this page loaded with — so the same request is safe to repeat once with the token it sent back.
  const freshCsrfToken = response.status === 422 && !isRetry ? getFreshCsrfToken(json) : null
  if (freshCsrfToken) {
    setCsrfToken(freshCsrfToken)
    return request<T>(method, url, body, true)
  }
  if (response.status === 403) {
    window.dispatchEvent(new CustomEvent(API_FORBIDDEN_EVENT, { detail: getForbiddenMessage(json) }))
  }
  if (!response.ok || !parsed.ok) {
    throw new ApiError(response.status, json, text)
  }
  return json as T
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  delete: <T>(url: string, body?: unknown) => request<T>('DELETE', url, body),
}
